// Whose money it is: an account belongs to its owners in equal parts; one nobody owns counts only
// for the household as a whole (DECISIONS D-027).

/** The part of an account's balance to count, or null when the account does not count at all. */
export type BalancePart = (accountId: string, balanceMinor: bigint) => bigint | null;

/** Accounts nobody owns: shown on their own next to the persons. */
export const UNASSIGNED = 'UNASSIGNED';

/**
 * One owner's part of a balance split equally among `owners`. The pennies that do not divide go
 * to the first owners in the list, so the parts always add up to the balance exactly.
 */
export function equalPart(balanceMinor: bigint, owners: readonly string[], personId: string): bigint | null {
  const index = owners.indexOf(personId);
  if (index < 0) return null;
  const count = BigInt(owners.length);
  const each = balanceMinor / count;
  const rest = balanceMinor - each * count;
  const extra = rest < 0n ? -1n : 1n;
  const pennies = rest < 0n ? -rest : rest;
  return BigInt(index) < pennies ? each + extra : each;
}

/** What counts for one person (or for {@link UNASSIGNED}): their part of each account they own. */
export function partOf(ownersOf: (accountId: string) => readonly string[] | undefined, who: string): BalancePart {
  return (accountId, balanceMinor) => {
    const owners = ownersOf(accountId) ?? [];
    if (who === UNASSIGNED) return owners.length === 0 ? balanceMinor : null;
    return equalPart(balanceMinor, owners, who);
  };
}
